import { Injectable, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ConsentComponent } from '../shared/modals/consent/consent.component';

declare let gtag: Function;

// Adapted from the portal's AnalyticsService; consent storage must stay compatible with it.
@Injectable({ providedIn: 'root' })
export class AnalyticsService implements OnDestroy {
  readonly COOKIES_ACCEPTED_KEY_NAME = 'b1_consent';
  // True until the user has made an initial consent choice; the app content stays hidden meanwhile.
  readonly consentRequired = signal(false);
  private readonly gaMeasurementId = (environment as { gaMeasurementId?: string }).gaMeasurementId;
  private readonly consentVersion = 1;
  private readonly consentLifetime = 180 * 24 * 60 * 60 * 1000;
  private consentDialogRef: MatDialogRef<ConsentComponent, boolean> | null = null;
  private analyticsConsentGranted = false;
  private rejectedWithoutStorage = false;
  private injected = false;
  private pendingEvents: Array<{ name: string; params: Record<string, unknown> }> = [];
  private pageViewsTrackingStarted = false;
  private lastLoggedPagePath: string | null = null;
  private readonly refreshConsent = () => { void this.tryInjectGtag(); };
  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.refreshConsent();
  };
  private readonly onStorage = (event: StorageEvent) => {
    if (!this.sharedConsentDomain && event.key === this.COOKIES_ACCEPTED_KEY_NAME) this.refreshConsent();
  };

  constructor(private router: Router, private dialog: MatDialog) {
    window.addEventListener('focus', this.refreshConsent);
    window.addEventListener('storage', this.onStorage);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.consentRequired.set(this.checkConsent() === null);
    void this.tryInjectGtag();
  }

  ngOnDestroy(): void {
    window.removeEventListener('focus', this.refreshConsent);
    window.removeEventListener('storage', this.onStorage);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private get sharedConsentDomain(): string | null {
    const hostname = window.location.hostname;
    return hostname === 'business-one.cloud' || hostname.endsWith('.business-one.cloud')
      ? 'business-one.cloud' : null;
  }

  async tryInjectGtag(): Promise<void> {
    const consent = this.checkConsent();
    if (consent === null) {
      this.updateConsent(false);
      this.openCookieConsentDialog();
      return;
    }
    this.consentRequired.set(false);
    this.consentDialogRef?.close();
    if (consent === true) {
      await this.injectGtag();
      this.flushPendingTracking();
      this.trackPageViews();
      this.trackCurrentPageView();
    } else {
      this.updateConsent(false);
    }
  }

  openCookieConsentDialog(): void {
    if (this.consentDialogRef) return;
    this.consentDialogRef = this.dialog.open<ConsentComponent, { accepted: boolean | null }, boolean>(ConsentComponent, {
      panelClass: 'consent-dialog',
      disableClose: this.consentRequired(),
      data: { accepted: this.checkConsent() },
    });
    this.consentDialogRef.afterClosed().subscribe(async (accepted) => {
      this.consentDialogRef = null;
      // Backdrop/Escape closes are not a consent choice and must not overwrite the stored value.
      if (typeof accepted !== 'boolean') return;

      this.consentRequired.set(false);
      this.rejectedWithoutStorage = false;
      try {
        const value = this.generateConsentCookie(accepted);
        if (this.sharedConsentDomain) {
          document.cookie = `${this.COOKIES_ACCEPTED_KEY_NAME}=${encodeURIComponent(value)}; Domain=${this.sharedConsentDomain}; Path=/; Max-Age=${this.consentLifetime / 1000}; Secure; SameSite=Lax`;
        } else {
          localStorage.setItem(this.COOKIES_ACCEPTED_KEY_NAME, value);
        }
      } catch {}
      const stored = this.checkConsent();
      this.rejectedWithoutStorage = !accepted && stored !== false;
      const savedAcceptance = accepted && stored === true;
      this.updateConsent(savedAcceptance);

      if (savedAcceptance) {
        await this.injectGtag();
        this.flushPendingTracking();
        this.logEvent('consent_given');
        this.trackPageViews();
        this.trackCurrentPageView();
      } else {
        this.pendingEvents = [];
      }
    });
  }

  logEvent(eventName: string, params: Record<string, unknown> = {}): void {
    if (typeof gtag === 'function' && this.checkConsent() === true) {
      this.updateConsent(true);
      gtag('event', eventName, params);
      return;
    }
    if (this.checkConsent() !== false) {
      this.pendingEvents.push({ name: eventName, params });
    }
  }

  private async injectGtag(): Promise<void> {
    if (this.checkConsent() !== true) return;
    if (this.injected) { this.updateConsent(true); return; }
    if (!this.gaMeasurementId) {
      console.warn('GA Measurement ID not set in environment.');
      return;
    }
    this.injected = true;
    this.updateConsent(true);
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaMeasurementId}`;
    document.head.appendChild(gtagScript);
    const inlineScript = document.createElement('script');
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
      gtag('consent', 'update', { analytics_storage: 'granted' });
      gtag('js', new Date());
      gtag('config', '${this.gaMeasurementId}', { send_page_view: false, cookie_domain: 'none', cookie_flags: 'SameSite=Lax;Secure' });
    `;
    document.head.appendChild(inlineScript);
    while (typeof gtag !== 'function') {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  private generateConsentCookie(value: boolean): string {
    const now = Date.now();
    return JSON.stringify({
      version: this.consentVersion,
      analytics: value,
      createdAt: now,
      expiresAt: now + this.consentLifetime,
    });
  }

  private checkConsent(): boolean | null {
    if (this.rejectedWithoutStorage) return false;
    let result: boolean | null = null;
    try {
      const entries = this.sharedConsentDomain
        ? document.cookie.split(';').map(part => part.trim()).filter(part => part.startsWith(this.COOKIES_ACCEPTED_KEY_NAME + '='))
        : [];
      const raw = this.sharedConsentDomain
        ? entries.length === 1 ? decodeURIComponent(entries[0].slice(this.COOKIES_ACCEPTED_KEY_NAME.length + 1)) : null
        : localStorage.getItem(this.COOKIES_ACCEPTED_KEY_NAME);
      const value = raw ? JSON.parse(raw) : null;
      const now = Date.now();
      if (value && value.version === this.consentVersion && typeof value.analytics === 'boolean' &&
        Number.isFinite(value.createdAt) && Number.isFinite(value.expiresAt) && value.createdAt <= now &&
        value.expiresAt > now && value.expiresAt > value.createdAt && value.expiresAt <= value.createdAt + this.consentLifetime) {
        result = value.analytics;
      }
    } catch {}
    if (result !== true && this.analyticsConsentGranted) this.updateConsent(false);
    return result;
  }

  private trackPageViews(): void {
    if (this.pageViewsTrackingStarted) return;
    this.pageViewsTrackingStarted = true;
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.logPageView(event.urlAfterRedirects));
  }

  private trackCurrentPageView(): void {
    this.logPageView(this.router.url || window.location.pathname);
  }

  private logPageView(pagePath: string): void {
    if (pagePath === this.lastLoggedPagePath) return;
    this.lastLoggedPagePath = pagePath;
    this.logEvent('page_view', {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  private flushPendingTracking(): void {
    if (typeof gtag !== 'function' || this.checkConsent() !== true) return;
    for (const event of this.pendingEvents) {
      gtag('event', event.name, event.params);
    }
    this.pendingEvents = [];
  }

  private updateConsent(accepted: boolean): void {
    const changed = this.analyticsConsentGranted !== accepted;
    this.analyticsConsentGranted = accepted;
    if (this.gaMeasurementId) (window as unknown as Record<string, unknown>)[`ga-disable-${this.gaMeasurementId}`] = !accepted;
    if (!accepted) {
      this.pendingEvents = [];
      this.lastLoggedPagePath = null;
      try {
        for (const entry of document.cookie.split(';')) {
          const name = entry.trim().split('=')[0];
          if (!/^_ga(?:_|$)/.test(name)) continue;
          const domains = window.location.hostname.split('.');
          document.cookie = `${name}=; Path=/; Max-Age=0; Secure; SameSite=Lax`;
          for (let index = 0; index < domains.length - 1; index++) {
            document.cookie = `${name}=; Domain=${domains.slice(index).join('.')}; Path=/; Max-Age=0; Secure; SameSite=Lax`;
          }
        }
      } catch {}
    }
    if (changed && typeof gtag === 'function') {
      gtag('consent', 'update', { analytics_storage: accepted ? 'granted' : 'denied' });
    }
  }
}
